import React, { useEffect } from "react";
import styled from "styled-components";
import { InputAdornment } from "@mui/material";
import { CountryCodeDropdown, DsTextField } from "../../../common";
import { Controller, useFormContext } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import { useCountryCodes } from "../../../pageComponents/reservationPage/hooks/useCountryCodes";
import { colors, fonts } from "../../common/mixins";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";

const Row = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  align-items: flex-start;
`;

export const CountryCodePrefix = styled.span`
  ${fonts.normalText()};
  color: ${colors.darkText};
  white-space: nowrap;
  user-select: none;
`;

export const PhoneAdornment = styled(InputAdornment)`
  && {
    margin-right: -6px;
  }
`;

type Props = {
  label?: string;
  disabled?: boolean;
};

export function PhoneNumberField({ disabled }: Props) {
  const r = useReservationResources();
  const {
    register,
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<ReservationFormValues>();
  const { countryCodes, defaultCountry } = useCountryCodes();

  useEffect(() => {
    if (getValues("contact.mobileCountryCode") !== undefined) return;
    const match =
      countryCodes.find((o) => o.code === defaultCountry) ??
      countryCodes.find((o) => o.code === "GR") ??
      countryCodes[0];
    if (match) setValue("contact.mobileCountryCode", `+${match.prefix}`);
  }, [countryCodes, defaultCountry, getValues, setValue]);

  return (
    <Controller
      control={control}
      name="contact.mobileCountryCode"
      render={({ field }) => (
        <Row>
          <CountryCodeDropdown
            options={countryCodes}
            value={
              countryCodes.find((o) => `+${o.prefix}` === field.value) ?? null
            }
            defaultValue={defaultCountry}
            onChange={(opt) => field.onChange(`+${opt.prefix}`)}
            disabled={disabled}
          />
          <DsTextField
            label={r.MobileNumber}
            type="tel"
            autoComplete="tel-national"
            required
            disabled={disabled}
            error={!!errors.contact?.mobile}
            helperText={errors.contact?.mobile?.message}
            InputProps={{
              startAdornment: field.value ? (
                <PhoneAdornment position="start">
                  <CountryCodePrefix>{field.value}</CountryCodePrefix>
                </PhoneAdornment>
              ) : undefined,
            }}
            {...register("contact.mobile", {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e?.target != null) {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }
                if (errors.contact?.mobile) {
                  if (e?.target?.value) trigger("contact.mobile");
                  else clearErrors("contact.mobile");
                }
              },
              onBlur: () => {
                if (getValues("contact.mobile") || errors.contact?.mobile)
                  trigger("contact.mobile");
              },
            })}
          />
        </Row>
      )}
    />
  );
}
